import React, { useCallback, useState } from 'react';

import { StyleSheet } from 'react-native';

import Modal from 'components/modal';
import { Box, Typography } from 'components/ui';
import useEffectOnce from 'hooks/use-effect-once';
import { CustomEventNames, EventTypes, logEvent } from 'state/amplitude';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { LANGUAGES } from 'state/intl/types';
import { getDesignToken, spacing } from 'styles/constants';
import { Region } from 'utils/environment/types';
import { setLanguageAndRestart } from 'utils/intl/language';

import LanguageSelectorModalContents from './language-selector-modal-contents';
import { ILanguageModalProps } from './types';

const LanguageSelectorModal: React.FC<React.PropsWithChildren<ILanguageModalProps>> = ({
  onModalDismiss,
  heading,
  primaryText,
  secondaryText,
  disclaimer,
}) => {
  const { region, language } = useLocale();
  const [{ langKey, regionKey }, setSelectedLocale] = useState({
    langKey: language,
    regionKey: region,
  });

  const handleSelection = (selectedLanguage: string, mappedRegion: string) => {
    setSelectedLocale({
      langKey: selectedLanguage as LANGUAGES,
      regionKey: mappedRegion as Region,
    });
  };

  const noChange = langKey === language;
  const setLanguageHandler = useCallback(() => {
    if (noChange) {
      onModalDismiss();
      return;
    }

    setLanguageAndRestart(langKey);
  }, [noChange, langKey, onModalDismiss]);

  useEffectOnce(() => {
    logEvent(CustomEventNames.LOCALE_SELECTOR_MODAL_SHOWN, EventTypes.Navigation);
  });

  const applyButtonText = t('apply');
  const modalHeading = heading || t('selectLanguage');

  return (
    <Modal
      testID="select-language-modal"
      onDismiss={onModalDismiss}
      amplitudeEventData={{
        modalAppearanceEventMessage: 'Language and region selector',
      }}
      header={modalHeading}
    >
      <Box style={styles.contentContainer}>
        {(primaryText || secondaryText) && (
          <Box style={styles.modalAdditionalContentWrapper}>
            {primaryText && (
              <Typography variant="copyOne" style={styles.primaryText}>
                {primaryText}
              </Typography>
            )}
            {secondaryText && (
              <Typography variant="copyOne" style={styles.secondaryText}>
                {secondaryText}
              </Typography>
            )}
          </Box>
        )}
        <LanguageSelectorModalContents
          ariaRadioGroupLabeledBy="language-select-modal-heading"
          regionKey={regionKey}
          langKey={langKey}
          disclaimer={disclaimer}
          applyButtonText={applyButtonText}
          setLanguage={setLanguageHandler}
          handleSelection={handleSelection}
        />
      </Box>
    </Modal>
  );
};

export default LanguageSelectorModal;

const styles = StyleSheet.create({
  contentContainer: {
    backgroundColor: getDesignToken('background-dialog'),
    paddingBottom: spacing.$8,
    paddingTop: spacing.$13_5,
  },
  modalAdditionalContentWrapper: {
    paddingHorizontal: spacing.$5,
  },
  primaryText: {
    marginHorizontal: 'auto',
    marginVertical: spacing.$2,
    textAlign: 'center',
  },
  secondaryText: {
    textAlign: 'center',
  },
});
